import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { SubGroup } from '~/components/Differentiation/types/subgroups-response.types';
import type { Accommodation } from '~/components/Differentiation/types/subgroups-payload.types';
import keyBy from 'lodash/keyBy';
import ClassAPIService from '~/services/apis/ClassAPIService';
import GameAPIService from '~/services/apis/GameAPIService';
import Analytics from '~/services/EventLoggerService';
import FeatureFlags from '~/services/FeatureFlagsService';
import FeatureFlagNames from '~/config/Constants/FeatureFlags';
import isEmpty from 'lodash/isEmpty';

export enum GameAccommodationsMode {
  GROUPS = 'groups',
  INDIVIDUALS = 'individuals',
  NONE = 'none',
}

export interface GameAccomodateGroup {
  groupId: string,
  name: string,
  subgroups: SubGroup[], // has accommodations
  totalMembers: number,
  totalAccommodatedMembers: number,
  enabled: boolean,
}

export interface GameAccommodatedIndividual {
  userId: string,
  name: string,
  accommodations?: Accommodation,
  enabled: boolean,
}

export interface GameAccomodationsStudent {
  userId: string,
  name: string,
}

const useGameAccommodationsStore = defineStore('gameAccommodationsStore', () => {
  const source = ref<string>('');
  const gameId = ref('');
  const isLoaded = ref(false);
  const mode = ref(GameAccommodationsMode.NONE);
  const groups = ref<GameAccomodateGroup[]>([]);
  const individuals = ref<GameAccommodatedIndividual[]>([]);
  const accommodatedGroupIds = ref<string[]>([]);
  const finalAccommodatedGroupIds = ref<string[]>([]);
  const groupIds = ref<string[]>([]);
  const groupBeingEdited = ref<string | null>(null);

  function initGroups(_groupIds: string[], _accommodatedGroupIds: string[]) {
    const enabledMap = keyBy(_accommodatedGroupIds, id => id);
    groupIds.value = _groupIds;
    groups.value = _groupIds.map((id) => {
      return {
        groupId: id,
        subgroups: [],
        enabled: !!enabledMap[id],
        name: '',
        totalMembers: 0,
        totalAccommodatedMembers: 0,
      };
    });

    accommodatedGroupIds.value = _accommodatedGroupIds;
    mode.value = GameAccommodationsMode.GROUPS;
  }

  function setSource(value: string) {
    source.value = value;
  }

  function setGroupBeingEdited(value: string | null) {
    groupBeingEdited.value = value;
  }

  function initIndividuals(students: GameAccomodationsStudent[]) {
    individuals.value = students.map((student) => {
      return {
        userId: student.userId,
        name: student.name,
        enabled: false,
      };
    });

    mode.value = GameAccommodationsMode.INDIVIDUALS;
  }

  function init(params: { gameId: string; groupIds: string[]; accommodatedGroupIds: string[]; students: GameAccomodationsStudent[] }) {
    gameId.value = params.gameId;
    groups.value = [];
    individuals.value = [];
    if (params.groupIds.length) {
      initGroups(params.groupIds, params.accommodatedGroupIds);
    } else if (params.students.length) {
      initIndividuals(params.students);
    } else {
      mode.value = GameAccommodationsMode.NONE;
    }
  }

  function updateGroups(docs: (Partial<GameAccomodateGroup> & { groupId: string })[]) {
    const keyById = keyBy(docs, doc => doc.groupId);
    groups.value = groups.value.map((group) => {
      if (!keyById[group.groupId]) {
        return group;
      }
      return {
        ...group,
        ...keyById[group.groupId],
      };
    });
  }

  function updateIndividuals(docs: (Partial<GameAccommodatedIndividual> & { _id: string })[]) {
    const keyById = keyBy(docs, doc => doc._id);
    individuals.value = individuals.value.map((individual) => {
      if (!keyById[individual.userId]) {
        return individual;
      }
      return {
        ...individual,
        ...keyById[individual.userId],
      };
    });
  }

  function isGroupsMode() {
    return mode.value === GameAccommodationsMode.GROUPS;
  }

  function isIndividualsMode() {
    return mode.value === GameAccommodationsMode.INDIVIDUALS;
  }

  function isEnabled() {
    const featureFlagValue = FeatureFlags().getFeatureValue(FeatureFlagNames.DIFFERENTIATION);
    return mode.value === GameAccommodationsMode.GROUPS && featureFlagValue !== 'DISABLED';
  }

  async function loadGroups() {
    groups.value = await Promise.all(groups.value.map(async (group) => {
      const response = await ClassAPIService.getClass(group.groupId);
      const subgroups = ((response?.group as any)?.subGroups || []) as SubGroup[];
      const members = response?.group?.members || [];
      const membersWithSubgroups = members.filter(member => (member as any).subGroupId);
      const totalAccommodatedMembers = membersWithSubgroups.length || 0;
      let enabled = group.enabled;
      if (group.groupId === groupBeingEdited.value || (source.value.includes('after-assign') || source.value.includes('new-assign-modal'))) {
        enabled = totalAccommodatedMembers > 0;
      }
      let membersCount = members.length || 0;
      if (response?.group.isGoogle && !isEmpty(response?.group?.google)) {
        membersCount = response?.group?.google?.studentDetails?.length || 0;
      }
      return {
        ...group,
        name: response?.group?.name || '',
        totalMembers: membersCount,
        totalAccommodatedMembers,
        subgroups,
        enabled,
      };
    }));
    setGroupBeingEdited(null);
  }

  async function updateGroupsAPI() {
    const enabledGroupIds = groups.value.filter(group => group.enabled && group.totalAccommodatedMembers > 0).map(group => group.groupId);
    finalAccommodatedGroupIds.value = enabledGroupIds;
    await GameAPIService.updateGroupAccommodations(gameId.value, enabledGroupIds);
  }

  async function loadIndividuals() {

  }

  async function updateIndividualsAPI() {}

  async function load() {
    isLoaded.value = false;
    if (isGroupsMode()) {
      await loadGroups();
    }
    if (isIndividualsMode()) {
      await loadIndividuals();
    }
    isLoaded.value = true;
  }

  async function update() {
    isLoaded.value = false;
    // check here to start a game with accomodations instead of updating game data
    if (isGroupsMode()) {
      await updateGroupsAPI();
    }
    if (isIndividualsMode()) {
      await updateIndividualsAPI();
    }
    isLoaded.value = true;
  }

  async function reset() {
    isLoaded.value = false;
  }

  const sendEvent = (eventName: string, extraParams: Record<string, unknown>) => {
    const params = {
      source: source.value,
      gameId: gameId.value,
      groupIds: groupIds.value,
      initialAccommodatedGroupIds: accommodatedGroupIds.value,
      finalAccommodatedGroupIds: finalAccommodatedGroupIds.value,
      ...extraParams,
    };
    Analytics.logEvent(eventName, params);
  };

  return {
    initIndividuals,
    initGroups,
    updateGroups,
    updateIndividuals,
    isGroupsMode,
    isIndividualsMode,
    init,
    gameId,
    mode,
    groups,
    individuals,
    isEnabled,
    load,
    isLoaded,
    reset,
    update,
    setSource,
    sendEvent,
    groupIds,
    setGroupBeingEdited,
    accommodatedGroupIds,
    finalAccommodatedGroupIds,
  };
});

export default useGameAccommodationsStore;
